import React from "react";
import { Link } from "react-router-dom";
import { BuilderSection, SiteHeaderBar, SiteFooterBar } from "./BuilderSections";
import ResponsiveScaler from "./ResponsiveScaler";
import AppInstallBanner from "../common/AppInstallBanner";

// Single source of truth for rendering admin-built home content — used by both
// the public homepage and the admin editor's Preview dialog, so the two can
// never drift apart. Whatever this renders IS the site.
export type HomeContentViewProps = {
    homeContent: any; // { sections, pagebg, header, footer }
    liveVideos: any[];
    pageVideos: any[];
    events: any[];
    logo?: string;
    // Only meaningful as a hard override to force it OFF (e.g. the admin
    // editor's Preview dialog, where "sticky" would stick to the dialog's own
    // scroll container, not the page). Otherwise stickiness is the admin's
    // own header.sticky setting (see HomeContentPage.tsx's Header toolbar),
    // defaulting to on for any home content saved before that setting existed.
    sticky?: boolean;
    // Forwarded to the internal ResponsiveScaler — forces a specific design
    // width to simulate (admin Preview's tablet/mobile/large-desktop modes)
    // instead of the real window width.
    forceWidth?: number;
};

const HomeContentView: React.FC<HomeContentViewProps> = ({ homeContent, liveVideos, pageVideos, events, logo, sticky, forceWidth }) => {
    const sections: any[] = homeContent?.sections || [];
    const headerCfg = homeContent?.header || {};
    const footerCfg = homeContent?.footer || {};
    const headerCustom = headerCfg.custom?.elements?.length ? headerCfg.custom : null;
    const footerCustom = footerCfg.custom?.elements?.length ? footerCfg.custom : null;
    const isSticky = sticky === false ? false : headerCfg.sticky !== false;
    const pageBgImage: string = homeContent?.pagebgimage || "";
    const pageBgImageFixed = homeContent?.pagebgimagefixed !== false;

    const footerLinks = (
        <div className="flex items-center gap-1">
            <Link to="/privacypolicy" className="hover:text-white transition">Privacy</Link>
            <span>/</span>
            <Link to="/termsandcondition" className="hover:text-white transition">Terms</Link>
            <span>/</span>
            {/* Plain <a>, not a react-router Link — this is a real static
                page under /public (see public/samaj-in-gujarat.html), not an
                SPA route, so it needs a full navigation. Also gives Google a
                real internal link into that page from the homepage. */}
            <a href="/samaj-in-gujarat.html" className="hover:text-white transition">Samaj in Gujarat</a>
        </div>
    );

    return (
        // Full viewport width, OUTSIDE ResponsiveScaler's 1440px design-canvas
        // cap — a page backdrop should bleed edge to edge on wide screens, not
        // stop at the design width like the content does. Sections with their
        // own explicit (opaque) bg still cover it as they scroll into place;
        // sections left at "Transparent" (see the section Bg Color control)
        // let it show through, including in this width margin either side.
        <div
            style={{
                background: homeContent?.pagebg || undefined,
                backgroundImage: pageBgImage ? `url(${pageBgImage})` : undefined,
                backgroundSize: pageBgImage ? "cover" : undefined,
                backgroundPosition: pageBgImage ? "center" : undefined,
                backgroundRepeat: pageBgImage ? "no-repeat" : undefined,
                backgroundAttachment: pageBgImage && pageBgImageFixed ? "fixed" : undefined,
                minHeight: "100vh",
            }}
        >
            <ResponsiveScaler forceWidth={forceWidth}>
                {headerCustom ? (
                    <div className={isSticky ? "sticky top-0 z-10 shadow-md" : undefined}>
                        <BuilderSection section={headerCustom} liveVideos={[]} pageVideos={[]} events={[]} fixedElementSizes />
                    </div>
                ) : (
                    <div className={isSticky ? "sticky top-0 z-10 shadow-md" : undefined}>
                        <SiteHeaderBar header={headerCfg} logo={logo} />
                    </div>
                )}

                {sections.map((section) => (
                    <BuilderSection key={section.id} section={section} liveVideos={liveVideos} pageVideos={pageVideos} events={events} pagebg={homeContent?.pagebg} />
                ))}

                {homeContent?.showinstallbanner && <AppInstallBanner forceShow />}

                {footerCustom ? (
                    <BuilderSection section={footerCustom} liveVideos={[]} pageVideos={[]} events={[]} fixedElementSizes />
                ) : (
                    <SiteFooterBar footer={footerCfg}>{footerLinks}</SiteFooterBar>
                )}
            </ResponsiveScaler>
        </div>
    );
};

export default HomeContentView;
